// src/components/Cart.js

import React, { useState } from 'react';
import {
  Container,
  Typography,
  List,
  Box,
  IconButton,
  TextField,
  Divider,
  Button,
  Alert
} from '@mui/material';
import { Add, Remove, Delete, NoteAdd } from '@mui/icons-material';
import { useCart } from '../CartContext';
import { useNavigate } from 'react-router-dom';

const TAX_RATE = 0.08; // 8% Ohio dine-in tax

export default function Cart() {
  const {
    cartItems,
    updateQuantity,
    removeFromCart,
    updateNote,
    deleteNote,
    orderType
  } = useCart();

  const navigate = useNavigate();
  const [noteInputs, setNoteInputs] = useState({});
  const [deletedItem, setDeletedItem] = useState('');

  // change qty, or remove when falling to 0
  const handleQuantityChange = (item, delta) => {
    const newQty = item.quantity + delta;
    if (newQty <= 0) {
      removeFromCart(item.name, item.glutenFreeOption);
      setDeletedItem(item.name);
      setTimeout(() => setDeletedItem(''), 3000);
    } else {
      updateQuantity(item.name, newQty, item.glutenFreeOption);
    }
  };

  // note text field local state
  const handleNoteChange = (itemName, value) => {
    setNoteInputs(n => ({ ...n, [itemName]: value }));
  };

  const saveNote = item => {
    updateNote(item.name, noteInputs[item.name] || '', item.glutenFreeOption);
    setNoteInputs(n => ({ ...n, [item.name]: '' }));
  };

  // compute subtotal/tax/total including upcharge
  const subtotal = cartItems.reduce((sum, i) => {
    const upcharge = i.glutenFreeOption && i.dietary?.glutenFreeOption ? 1 : 0;
    return sum + (i.price + upcharge) * i.quantity;
  }, 0);
  const tax   = orderType === 'Dine-In' ? +(subtotal * TAX_RATE).toFixed(2) : 0;
  const total = +(subtotal + tax).toFixed(2);

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Your Cart ({orderType})
      </Typography>

      {deletedItem && (
        <Alert severity="info" sx={{ mb: 2 }}>
          “{deletedItem}” was removed from your cart.
        </Alert>
      )}

      {cartItems.length === 0 ? (
        <Typography>Your cart is empty.</Typography>
      ) : (
        <>
          <List>
            {cartItems.map(item => (
              <Box
                key={`${item.name}-${item.glutenFreeOption}`}
                sx={{ mb: 2, p: 2, boxShadow: 2, borderRadius: 1 }}
              >
                {/* Quantity controls & remove */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={() => handleQuantityChange(item, -1)}>
                    <Remove />
                  </IconButton>

                  <Typography variant="body1" sx={{ mx: 1 }}>
                    {item.quantity}
                  </Typography>

                  <IconButton onClick={() => handleQuantityChange(item, +1)}>
                    <Add />
                  </IconButton>

                  <Box sx={{ flexGrow: 1, ml: 2 }}>
                    <Typography variant="subtitle1">{item.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ${(
                        item.price +
                        (item.glutenFreeOption && item.dietary?.glutenFreeOption ? 1 : 0)
                      ).toFixed(2)} × {item.quantity}
                    </Typography>
                    {item.glutenFreeOption && (
                      <Typography variant="caption" color="text.secondary">
                        (+$1 GF upcharge)
                      </Typography>
                    )}
                  </Box>

                  <IconButton
                    color="error"
                    onClick={() =>
                      removeFromCart(item.name, item.glutenFreeOption)
                    }
                  >
                    <Delete />
                  </IconButton>
                </Box>

                {/* Notes */}
                {item.note ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      Note: {item.note}
                    </Typography>
                    <IconButton
                      color="error"
                      onClick={() => deleteNote(item.name, item.glutenFreeOption)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', mt: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add a note (e.g., allergies)"
                      value={noteInputs[item.name] || ''}
                      onChange={e =>
                        handleNoteChange(item.name, e.target.value)
                      }
                    />
                    <IconButton
                      color="primary"
                      onClick={() => saveNote(item)}
                    >
                      <NoteAdd />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: 'right', mb: 2 }}>
            <Typography>Subtotal: ${subtotal.toFixed(2)}</Typography>
            {orderType === 'Dine-In' && (
              <Typography>Tax (8%): ${tax.toFixed(2)}</Typography>
            )}
            <Typography variant="h6">Total: ${total.toFixed(2)}</Typography>
          </Box>
        </>
      )}

      <Button
        variant="contained"
        fullWidth
        disabled={cartItems.length === 0}
        onClick={() => navigate('/payment')}
        sx={{ bgcolor: 'black', color: 'white', '&:hover': { bgcolor: '#333' } }}
      >
        Proceed to Payment
      </Button>
    </Container>
  );
}
